import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import LibraryDialog from '../Library/LibraryDialog';
import Chip from '../shared/Chip';
import Icon from '../shared/Icon';
import { ASSESSMENT_STATUS } from '../../../models/AssessmentsModel';
import AssessmentEvaluation from './AssessmentEvaluation';
import AssessmentOverview from './AssessmentOverview';
import { animateBox } from './animateBox';
import './AssessmentDetail.css';

// Said when a failed record carries no message of its own.
const FAILED_STEP = {
  upload: 'Upload failed.',
  processing: 'Processing failed.',
};

const TABS = [['comparison', 'Comparison'], ['overview', 'Overview']];

/* Every modal puts its actions in a row at the bottom right. Archive sits
   left of the primary, which is the way forward: Retry on a failure, Download
   report on a Completed assessment. */
function DetailActions({ assessment, allows, onArchive, onUnarchive, onRetry, onDownloadReport }) {
  const canReport = assessment.status === 'completed';
  const report = canReport && (
    <button type="button" className="resui-btn resui-btn--primary" onClick={() => onDownloadReport?.(assessment)}>Download report</button>
  );
  if (assessment.archived) {
    return (
      <div className="library-dialog-actions assessment-detail-actions">
        <button type="button" className="resui-btn resui-btn--secondary" onClick={() => onUnarchive?.(assessment)}>Unarchive</button>
        {report}
      </div>
    );
  }
  const canArchive = allows.includes('archive');
  const canRetry = allows.includes('retry');
  if (!canArchive && !canRetry && !canReport) return null;
  return (
    <div className="library-dialog-actions assessment-detail-actions">
      {canArchive && (
        <button type="button" className="resui-btn resui-btn--secondary assessment-btn-destructive" onClick={(e) => onArchive?.(assessment, e.currentTarget)}>Archive</button>
      )}
      {canRetry && (
        <button type="button" className="resui-btn resui-btn--primary" onClick={() => onRetry?.(assessment)}>Retry</button>
      )}
      {report}
    </div>
  );
}

/**
 * Assessment detail (Navy Phase 0, stories 23 to 26): one dialog for every
 * state. Uploading, Queued and Failed show the record's fields; Failed adds a
 * banner naming the step that failed. Processing and Completed open on the
 * comparison, two columns that each scroll in place, with Overview a tab away.
 * Switching tab keeps both panels mounted, so the clip plays on underneath.
 *
 * `scenario` is the Library scenario the record runs against and `documents`
 * the Library's documents. The actions are the caller's: Archive asks first,
 * and gets the button it came from so Cancel can return there. `covered` is
 * true while that confirm sits on top.
 */
function AssessmentDetail({ assessment, scenario, documents, opener, fallbackFocus, covered, onClose, ...actions }) {
  const meta = ASSESSMENT_STATUS[assessment.status] || { label: assessment.status, variant: 'standard', actions: [] };
  const evaluating = assessment.status === 'processing' || assessment.status === 'completed';
  const [tab, setTab] = useState('comparison');
  const wide = evaluating && tab === 'comparison';
  const bodyRef = useRef(null);
  const tabRef = useRef(null);
  const from = useRef(null);

  // The dialog eases between its two widths when the tab changes.
  useLayoutEffect(() => {
    const dialog = bodyRef.current && bodyRef.current.closest('.library-dialog');
    animateBox(dialog, from.current);
    from.current = null;
  }, [tab]);

  // The record moves on underneath an open dialog. When that takes away the
  // control that had focus (Retry, once the retry starts), focus would drop
  // to the page behind the scrim, so it comes back to the dialog.
  useEffect(() => {
    if (document.activeElement && document.activeElement !== document.body) return;
    const dialog = bodyRef.current && bodyRef.current.closest('.library-dialog');
    const close = dialog && dialog.querySelector('.library-close-btn');
    if (close) close.focus();
  }, [assessment.status, assessment.archived]);

  const selectTab = (next) => {
    if (next === tab) return;
    const dialog = bodyRef.current && bodyRef.current.closest('.library-dialog');
    from.current = dialog ? { width: dialog.offsetWidth, height: dialog.offsetHeight } : null;
    setTab(next);
  };

  const progress = assessment.status === 'uploading' ? ` ${Math.round(assessment.progress || 0)}%` : '';
  const failure = assessment.status === 'failed' && (assessment.error || FAILED_STEP[assessment.failedStep]);

  return (
    <LibraryDialog
      title={assessment.name}
      className={`assessment-detail${wide ? ' assessment-detail--wide' : ''}`}
      initialFocus={tabRef}
      covered={covered}
      opener={opener}
      fallbackFocus={fallbackFocus}
      onClose={onClose}
    >
      <div ref={bodyRef} className="assessment-detail-status">
        <Chip variant={meta.variant} className={assessment.archived ? 'is-archived' : ''}>{`${meta.label}${progress}`}</Chip>
      </div>
      {failure && (
        <div className="resui-banner resui-banner--info assessment-detail-banner">
          <Icon name="warning-circle" size={18} />
          <div className="resui-banner-text">{failure}</div>
        </div>
      )}
      {!evaluating ? (
        <AssessmentOverview assessment={assessment} scenario={scenario} documents={documents} />
      ) : (
        <>
          <div className="library-tabs assessment-detail-tabs">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                ref={id === tab ? tabRef : undefined}
                type="button"
                className={`library-tab${id === tab ? ' is-active' : ''}`}
                aria-current={id === tab ? 'true' : undefined}
                aria-controls={`assessment-panel-${id}-${assessment.id}`}
                data-label={label}
                onClick={() => selectTab(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div id={`assessment-panel-comparison-${assessment.id}`} className="assessment-detail-panel" hidden={tab !== 'comparison'}>
            <AssessmentEvaluation assessment={assessment} scenario={scenario} documents={documents} />
          </div>
          <div id={`assessment-panel-overview-${assessment.id}`} className="assessment-detail-panel" hidden={tab !== 'overview'}>
            <AssessmentOverview assessment={assessment} scenario={scenario} documents={documents} />
          </div>
        </>
      )}
      <DetailActions assessment={assessment} allows={meta.actions} {...actions} />
    </LibraryDialog>
  );
}

export default AssessmentDetail;
